import { MAX_VOICE_NOTE_DURATION_MS, type VoiceNoteContentType } from '../api/types';
import { pickVoiceMimeType } from './mime';

export interface VoiceRecordingResult {
  blob: Blob;
  contentType: VoiceNoteContentType;
  durationMs: number;
}

export interface VoiceRecorderHandle {
  /** Stops the recording and resolves with its bytes. Idempotent: calling
   *  it twice, or after an auto-stop already fired, resolves the same
   *  result rather than throwing. */
  stop: () => Promise<VoiceRecordingResult>;
  /** Discards the recording. `onAutoStop` and the `stop()` promise never
   *  fire for a cancelled clip. */
  cancel: () => void;
}

export interface StartVoiceRecordingOptions {
  stream: MediaStream;
  /** Called on a short tick with the time recorded so far, for an elapsed
   *  display. */
  onElapsed?: (elapsedMs: number) => void;
  /** Called once, only when INTERFACES.md 15.3's cap stops the clip rather
   *  than the operator. */
  onAutoStop?: (result: VoiceRecordingResult) => void;
  maxDurationMs?: number;
  /** Injectable for tests; default is a 250ms wall-clock tick. */
  tickMs?: number;
  now?: () => number;
  createRecorder?: (stream: MediaStream, options: MediaRecorderOptions) => MediaRecorder;
  setIntervalFn?: (callback: () => void, ms: number) => ReturnType<typeof setInterval>;
  clearIntervalFn?: (handle: ReturnType<typeof setInterval>) => void;
}

/**
 * Records one clip against `MAX_VOICE_NOTE_DURATION_MS` (INTERFACES.md
 * 15.3, 120 seconds), auto-stopping at the cap so a clip left running
 * never grows past what the server will accept.
 *
 * Every browser dependency is injectable (`createRecorder`, `now`, the
 * timer functions), which is what lets `recorder.test.ts` drive the whole
 * start/tick/stop sequence with no real `MediaRecorder` and no real clock:
 * jsdom implements neither.
 */
export function startVoiceRecording(options: StartVoiceRecordingOptions): VoiceRecorderHandle {
  const {
    stream,
    onElapsed,
    onAutoStop,
    maxDurationMs = MAX_VOICE_NOTE_DURATION_MS,
    tickMs = 250,
    now = () => Date.now(),
    createRecorder = (mediaStream, recorderOptions) => new MediaRecorder(mediaStream, recorderOptions),
    setIntervalFn = setInterval,
    clearIntervalFn = clearInterval,
  } = options;

  const picked = pickVoiceMimeType();
  const contentType: VoiceNoteContentType = picked?.contentType ?? 'audio/webm';
  const recorder = createRecorder(stream, picked ? { mimeType: picked.mimeType } : {});
  const chunks: BlobPart[] = [];
  const startedAt = now();
  let stopRequested = false;
  let autoStopped = false;

  recorder.ondataavailable = (event: BlobEvent) => {
    if (event.data.size > 0) chunks.push(event.data);
  };

  let resolveStop: ((result: VoiceRecordingResult) => void) | null = null;
  const settled = new Promise<VoiceRecordingResult>((resolve) => {
    resolveStop = resolve;
  });

  recorder.onstop = () => {
    clearIntervalFn(tick);
    const durationMs = now() - startedAt;
    const result: VoiceRecordingResult = {
      blob: new Blob(chunks, { type: contentType }),
      contentType,
      durationMs,
    };
    resolveStop?.(result);
    if (autoStopped) onAutoStop?.(result);
  };

  recorder.start();

  const tick = setIntervalFn(() => {
    const elapsed = now() - startedAt;
    onElapsed?.(elapsed);
    if (elapsed >= maxDurationMs && !stopRequested) {
      stopRequested = true;
      autoStopped = true;
      recorder.stop();
    }
  }, tickMs);

  function stop(): Promise<VoiceRecordingResult> {
    if (!stopRequested) {
      stopRequested = true;
      recorder.stop();
    }
    return settled;
  }

  function cancel(): void {
    stopRequested = true;
    clearIntervalFn(tick);
    recorder.onstop = null;
    if (recorder.state !== 'inactive') recorder.stop();
  }

  return { stop, cancel };
}

/** `0:07`, `1:45`. `mm:ss`, minutes uncapped, because 120 seconds is the
 *  longest this app ever shows. */
export function formatElapsed(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
